import type { GeneratedIdentity, Locale, LocationRecord } from './types';
import { educationLevels, firstNames, generateCompany, hairColors, industries, jobs, lastNames, operatingSystems, securityQuestions, streetRoots, streetTypes } from './identityCatalog';
import { localizedStateName } from './states';
type CardType = 'Visa' | 'Mastercard' | 'American Express' | 'Discover';

const cardTypes: readonly CardType[] = ['Visa', 'Mastercard', 'American Express', 'Discover'];

const stateAreaCodes: Record<string, readonly string[]> = {
  AL: ['205', '251', '256', '334', '659', '938'], AK: ['907'], AZ: ['480', '520', '602', '623', '928'], AR: ['479', '501', '870'],
  CA: ['209', '213', '279', '310', '323', '341', '408', '415', '424', '442', '510', '530', '559', '562', '619', '626', '628', '650', '657', '661', '669', '707', '714', '747', '760', '805', '818', '820', '831', '840', '858', '909', '916', '925', '949', '951'],
  CO: ['303', '719', '720', '970', '983'], CT: ['203', '475', '860', '959'], DE: ['302'], DC: ['202', '771'], FL: ['239', '305', '321', '352', '386', '407', '448', '561', '656', '689', '727', '754', '772', '786', '813', '850', '863', '904', '941', '954'],
  GA: ['229', '404', '470', '478', '678', '706', '762', '770', '912', '943'], HI: ['808'], ID: ['208', '986'], IL: ['217', '224', '309', '312', '331', '447', '464', '618', '630', '708', '730', '773', '779', '815', '847', '861', '872'],
  IN: ['219', '260', '317', '463', '574', '765', '812', '930'], IA: ['319', '515', '563', '641', '712'], KS: ['316', '620', '785', '913'], KY: ['270', '364', '502', '606', '859'], LA: ['225', '318', '337', '504', '985'],
  ME: ['207'], MD: ['227', '240', '301', '410', '443', '667'], MA: ['339', '351', '413', '508', '617', '774', '781', '857', '978'], MI: ['231', '248', '269', '313', '517', '586', '616', '679', '734', '810', '906', '947', '989'],
  MN: ['218', '320', '507', '612', '651', '763', '952'], MS: ['228', '601', '662', '769'], MO: ['314', '417', '557', '573', '636', '660', '816', '975'], MT: ['406'], NE: ['308', '402', '531'], NV: ['702', '725', '775'],
  NH: ['603'], NJ: ['201', '551', '609', '640', '732', '848', '856', '862', '908', '973'], NM: ['505', '575'], NY: ['212', '315', '329', '332', '347', '363', '516', '518', '585', '607', '624', '631', '646', '680', '716', '718', '838', '845', '914', '917', '929', '934'],
  NC: ['252', '336', '472', '704', '743', '828', '910', '919', '980', '984'], ND: ['701'], OH: ['216', '220', '234', '283', '326', '330', '380', '419', '440', '513', '567', '614', '740', '937'], OK: ['405', '539', '572', '580', '918'],
  OR: ['458', '503', '541', '971'], PA: ['215', '223', '267', '272', '412', '445', '484', '570', '582', '610', '717', '724', '814', '835', '878'], RI: ['401'], SC: ['803', '839', '843', '854', '864'], SD: ['605'],
  TN: ['423', '615', '629', '731', '865', '901', '931'], TX: ['210', '214', '254', '281', '325', '346', '361', '409', '430', '432', '469', '512', '682', '713', '726', '737', '806', '817', '830', '832', '903', '915', '936', '940', '945', '956', '972', '979'],
  UT: ['385', '435', '801'], VT: ['802'], VA: ['276', '434', '540', '571', '686', '703', '757', '804', '826', '948'], WA: ['206', '253', '360', '425', '509', '564'], WV: ['304', '681'], WI: ['262', '274', '353', '414', '534', '608', '715', '920'], WY: ['307'],
  AS: ['684'], GU: ['671'], MP: ['670'], PR: ['787', '939'], VI: ['340'],
};

export const pick = <T>(items: readonly T[], random = Math.random): T => items[Math.floor(random() * items.length)];
const int = (min: number, max: number, random = Math.random) => Math.floor(random() * (max - min + 1)) + min;
const pad = (value: number, length: number) => value.toString().padStart(length, '0');

function cardPrefix(cardType: CardType, random = Math.random): string {
  if (cardType === 'Visa') return '4';
  if (cardType === 'Mastercard') return random() < 0.5 ? String(int(51, 55, random)) : String(int(2221, 2720, random));
  if (cardType === 'American Express') return pick(['34', '37'], random);
  return pick(['6011', '65', String(int(644, 649, random))], random);
}

export function isLuhnValid(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (!digits || /^(\d)\1+$/.test(digits)) return false;
  const sum = [...digits].reverse().reduce((total, digit, index) => {
    let number = Number(digit);
    if (index % 2 === 1) {
      number *= 2;
      if (number > 9) number -= 9;
    }
    return total + number;
  }, 0);
  return sum % 10 === 0;
}

function appendLuhnCheckDigit(partial: string): string {
  for (let checkDigit = 0; checkDigit <= 9; checkDigit += 1) {
    const candidate = `${partial}${checkDigit}`;
    if (isLuhnValid(candidate)) return candidate;
  }
  throw new Error('Unable to generate Luhn check digit');
}

function formatCardNumber(cardType: CardType, digits: string): string {
  if (cardType === 'American Express') return `${digits.slice(0, 4)} ${digits.slice(4, 10)} ${digits.slice(10)}`;
  return digits.match(/.{1,4}/g)?.join(' ') ?? digits;
}

export function matchesCardType(cardType: string, value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (cardType === 'Visa') return /^4\d{15}$/.test(digits);
  if (cardType === 'Mastercard') {
    const firstFour = Number(digits.slice(0, 4));
    return digits.length === 16 && (/^5[1-5]/.test(digits) || (firstFour >= 2221 && firstFour <= 2720));
  }
  if (cardType === 'American Express') return /^3[47]\d{13}$/.test(digits);
  if (cardType === 'Discover') return digits.length === 16 && (/^6011/.test(digits) || /^65/.test(digits) || /^64[4-9]/.test(digits));
  return false;
}

function generateCard(random = Math.random) {
  const cardType = pick(cardTypes, random);
  const length = cardType === 'American Express' ? 15 : 16;
  let partial = cardPrefix(cardType, random);
  while (partial.length < length - 1) partial += String(int(0, 9, random));
  const digits = appendLuhnCheckDigit(partial);
  return {
    cardType,
    cardNumber: formatCardNumber(cardType, digits),
    cvvLength: cardType === 'American Express' ? 4 : 3,
  };
}

export function generateIdentity(location: LocationRecord, locale: Locale, requireHouseNumber = true, random = Math.random): GeneratedIdentity {
  const first = pick(firstNames, random);
  const last = pick(lastNames, random);
  const gender = pick(['Female', 'Male', 'Non-binary'] as const, random);
  const title = gender === 'Female' ? 'Ms.' : gender === 'Male' ? 'Mr.' : 'Mx.';
  const birthYear = int(1964, 2005, random);
  const birthday = `${int(1, 12, random)}/${int(1, 28, random)}/${birthYear}`;
  const streetName = `${pick(streetRoots, random)} ${pick(streetTypes, random)}`;
  const street = requireHouseNumber || random() > 0.22 ? `${int(11, 9899, random)} ${streetName}` : streetName;
  const userStem = `${first}.${last}${int(17, 981, random)}`.toLowerCase();
  const emailDomain = pick(['gmail.com', 'outlook.com', 'hotmail.com'], random);
  const email = pick([
    `${first}${last}${int(10, 999, random)}`,
    `${first[0]}${last}${int(10, 99, random)}`,
    `${first}_${last}`,
    `${first}${last}${birthYear}`,
  ], random).toLowerCase() + `@${emailDomain}`;
  const areaCode = pick(stateAreaCodes[location.s] || ['202'], random);
  const [securityQuestion, securityAnswer] = pick(securityQuestions, random);
  const card = generateCard(random);
  const expiryMonth = int(1, 12, random);
  const expiryYear = new Date().getFullYear() + int(2, 6, random);
  const feet = int(5, 6, random);
  const inches = feet === 6 ? int(0, 4, random) : int(1, 11, random);
  const centimeters = Math.round((feet * 12 + inches) * 2.54);
  const pounds = int(108, 235, random);
  const kilograms = Math.round(pounds * 0.453592);

  return {
    schemaVersion: '1.0',
    generatedAt: new Date().toISOString(),
    locale,
    person: {
      fullName: `${first} ${last}`,
      gender,
      title,
      birthday,
      hairColor: pick(hairColors, random),
    },
    address: {
      country: 'United States',
      street,
      city: location.c,
      state: location.s,
      stateName: location.n,
      stateNameLocalized: localizedStateName(location.s, location.n),
      zipCode: location.z,
      latitude: location.a,
      longitude: location.o,
      phone: `+1 (${areaCode}) 555-01${pad(int(0, 99, random), 2)}`,
      email,
    },
    employment: {
      occupation: pick(jobs, random),
      company: generateCompany(random),
      industry: pick(industries, random),
      companySize: pick(['1-10', '11-50', '51-200', '201-500', '501-1,000', '1,001-5,000'], random),
      employmentStatus: pick(['Full-time', 'Part-time', 'Contract', 'Self-employed'], random),
      salary: `$${int(42, 186, random).toLocaleString('en-US')},${pad(int(0, 999, random), 3)}`,
    },
    paymentTestData: {
      cardType: card.cardType,
      cardNumber: card.cardNumber,
      cvv: pad(int(0, 10 ** card.cvvLength - 1, random), card.cvvLength),
      expiry: `${pad(expiryMonth, 2)}/${expiryYear}`,
      simulatedOnly: true,
    },
    additional: {
      username: userStem.replace('.', '_'),
      password: `${first.slice(0, 2)}!${last.slice(0, 3)}#${int(1000, 9999, random)}`,
      height: `${centimeters} cm (${feet}'${inches}\")`,
      weight: `${kilograms} kg (${pounds} lbs)`,
      bloodType: pick(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'], random),
      operatingSystem: pick(operatingSystems, random),
      guid: crypto.randomUUID(),
      userAgent: typeof navigator === 'undefined' ? 'Test Browser/1.0' : navigator.userAgent,
      education: pick(educationLevels, random),
      website: `https://${first.toLowerCase()}-${last.toLowerCase()}.example`,
      securityQuestion,
      securityAnswer,
    },
  };
}

export function flattenIdentity(identity: GeneratedIdentity): Array<[string, string]> {
  return [
    ...Object.entries(identity.person),
    ...Object.entries(identity.address).filter(([key]) => !['latitude', 'longitude'].includes(key)),
    ...Object.entries(identity.employment),
    ...Object.entries(identity.paymentTestData).filter(([key]) => key !== 'simulatedOnly'),
    ...Object.entries(identity.additional),
  ].map(([key, value]) => [key, String(value)]);
}
